import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import * as i18n from "../common/i18n";
import MaterialTable from 'material-table';
import { reactLocalStorage } from 'reactjs-localstorage';
import LinearProgress from '@material-ui/core/LinearProgress';
import { CsvBuilder } from 'filefy';
import gql from 'graphql-tag';
import Message from "./Message";
import * as graphql from '../common/graphql';
import * as helper from "../common/helper";
import moment from "moment";
import ReloadIcon from '@material-ui/icons/Replay';
import RouterIcon from '@material-ui/icons/Router';
import ChipLink from '../components/ChipLink';

import { withIAM, isInRoles, ROLE_ADMIN, ROLE_READER, ROLE_OWNER, ROLE_ASSISTANT, ROLE_EDITOR } from "../common/iam";

const styles = theme => ({
});


const defaults = {
    id: true,
    uuid: false,
    created: false,
    imei: false,
    pairMode: true,
    pageSize: 20,
};


const defaultsPropertyName = 'p71.pairings.defaults';

class Pairings extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
            userSettings: reactLocalStorage.getObject(defaultsPropertyName, defaults),
        };

        this.tableRef = React.createRef();
    }

    default(id) {
        return this.state.userSettings[id] !== undefined ? this.state.userSettings[id] : defaults[id];
    }

    setDefault(id, value) {

        const newUserSettings = { ...this.state.userSettings };
        newUserSettings[id] = value;

        this.setState({ userSettings: newUserSettings }, () => {
            reactLocalStorage.setObject(defaultsPropertyName, newUserSettings);
        });
    }

    exportCsv = async (columns, data) => {

        try {

            this.setState({
                exporting: true,
                progressValue: 0
            });

            const allPairings = [];
            let totalCount = 0;
            const first = 100;
            let offset = 0

            this.showMessage("Export wird angestoßen. Bitte verlassen Sie die Seite nicht, solange der Exportvorgang läuft.", "info");

            do {
                
                const filters = []
                for (const column of columns) {
                    if (column.tableData.filterValue !== undefined) {
                        filters.push({
                            column: column,
                            field: column.field,
                            type: column.type,
                            comparator: column.comparator,
                            value: column.tableData.filterValue
                        })
                    }
                }

                const find = helper.whereString(filters, this.mapField, ["id"]);

                const result = await graphql.clientWithToken(this.props.accessToken).query({
                    query: gql`
                    query {
                        pairings(first: 100, offset: ${offset}, where: ${find}, orderBy: [{column: "uuid" direction: ASC}]) {
                            totalCount
                            elements {
                                ... on Pairing {
                                    id
                                    uuid,
                                    created { X },
                                    pairMode,
                                    imei
                                }
                            }
                        }
                    }
                `,
                    fetchPolicy: "no-cache"
                });

                totalCount = result.data.pairings.totalCount;
                offset = offset + first;
                for (const pairing of result.data.pairings.elements) {
                    allPairings.push(
                        {
                            id: pairing.id,
                            created: pairing.created,
                            uuid: pairing.uuid,
                            imei: pairing.imei,
                            pairMode: pairing.pairMode
                        }
                    )
                }

                if (totalCount !== 0) {
                    this.setState({
                        exporting: true,
                        progressValue: (allPairings.length / totalCount) * 100
                    });
                }
            } while (allPairings.length < totalCount);

            const builder = new CsvBuilder('Pairings.csv')
                .setDelimeter(";")
                .setColumns(columns.map(columnDef => columnDef.title))
                .addRows(allPairings.map(pairing => { return [pairing.uuid, pairing.imei, pairing.created ? moment(pairing.created.X, "X").format('DD.MM.YYYY HH:mm') : '', pairing.pairMode, pairing.id] }))
                .exportFile();

            this.setState({
                exporting: false,
                progressValue: 0
            });
        } catch (error) {
            this.setState({
                exporting: false,
                progressValue: 0
            });
            this.showMessage(error.message, "error");
        }

    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    mapField(column) {
        return column;
    }

    render() {

        return (
            <div style={{ maxWidth: '100%' }}>

                {this.state.exporting && <div style={{ width: "100%" }}><LinearProgress value={this.state.progressValue} variant={"determinate"} /></div>}

                <MaterialTable
                    components={{
                        Cell: helper.defaultCell,
                        FilterRow: helper.defaultFilter
                    }}
                    icons={helper.tableIcons}
                    onChangeRowsPerPage={pageSize => { this.setDefault("pageSize", pageSize) }}
                    onChangeColumnHidden={data => { this.setDefault(data["field"], data["hidden"]) }}
                    tableRef={this.tableRef}
                    localization={i18n.getLocalization()}
                    columns={[
                        {
                            title: i18n.get("pairings.uuid"),
                            field: 'uuid',
                            hidden: this.default("uuid"),
                            cellStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            headerStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            sorting: true,
                            filtering: true,
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        icon={<RouterIcon />}
                                        link={{ page: i18n.get('menus.devices'), data: { uuid: rowData.uuid } }}
                                        key={rowData.uuid}
                                        label={`${rowData.uuid}`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("pairings.imei"),
                            field: 'imei',
                            hidden: this.default("imei"),
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            sorting: true,
                            filtering: true,
                        },
                        {
                            title: i18n.get("pairings.created"),
                            field: 'created',
                            hidden: this.default("created"),
                            type: 'datetime',
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            sorting: true,
                            filtering: true,
                            filterComponent: helper.DateRangeFilter,
                        },
                        {
                            title: i18n.get("pairings.pairMode"),
                            field: 'pairMode',
                            hidden: this.default("pairMode"),
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            sorting: true,
                            filtering: true,
                        },
                        {
                            title: i18n.get("pairings.id"),
                            field: 'id',
                            hidden: this.default("id"),
                            cellStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            headerStyle: {
                                minWidth: '200px',
                                maxWidth: '200px',
                            },
                            sorting: true,
                            filtering: true,
                        },
                    ]}
                    data={query =>
                        new Promise(async (resolve, reject) => {

                            try {

                                const where = helper.whereString(query['filters'], this.mapField, ["uuid"]);
                                let orderBy = helper.orderByString(query, this.mapField, 'uuid', query["orderBy"] && query["orderBy"]["field"] === "uuid");
                                if (query["orderBy"] && query["orderBy"]["field"] === "uuid" && where === "[]") {
                                    where = '[{column: "created", comparator: GT, datetime: -1}]'
                                }

                                const result = await graphql.clientWithToken(this.props.accessToken).query({
                                    query: gql`
                                    query {
                                        pairings(first: ${query.pageSize}, offset: ${query.pageSize * query.page}, orderBy:${orderBy}, where:${where}) {
                                            totalCount
                                            elements {
                                                ... on Pairing {
                                                    id
                                                    imei,
                                                    uuid,
                                                    created { X },
                                                    pairMode,
                                               }
                                            }
                                        }
                                    }
                                `,
                                    fetchPolicy: "no-cache"
                                });

                                const searchResult = {
                                    page: query.page,
                                    data: result.data.pairings.elements.map(
                                        (element) => {
                                            return {
                                                id: element.id,
                                                imei: element.imei,
                                                uuid: element.uuid,
                                                created: element.created ? moment(element.created.X, 'X').toDate() : null,
                                                pairMode: element.pairMode
                                            };
                                        }),
                                    totalCount: result.data.pairings.totalCount
                                }
                                resolve(searchResult);
                            } catch (error) {
                                this.showMessage(error.message, "error");
                                reject(error);
                            }
                        })
                    }
                    actions={[{
                        isFreeAction: true,
                        icon: () => { return <ReloadIcon /> },
                        tooltip: i18n.get("general.reload"),
                        onClick: async (event, rowData) => {
                            this.tableRef.current.onQueryChange();
                        }
                    }]}
                    title={i18n.get("pairings.title")}
                    options={{
                        addRowPosition: "first",
                        exportButton: !this.state.exporting,
                        filtering: true,
                        showTitle: false,
                        debounceInterval: 500,
                        search: false,
                        columnsButton: true,
                        exportCsv: this.exportCsv,
                        pageSize: this.default("pageSize"),
                        pageSizeOptions: [5, 20, 50]
                    }}

                />

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />
            </div>
        )
    }
}

Pairings.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(Pairings))